// RevaluationModal.js
import React, { useState, useEffect } from 'react';

const RevaluationModal = ({ 
    isOpen, 
    onClose, 
    onPaymentSuccess,
    user,
    studentData 
}) => {
    const [revaluationSubjects, setRevaluationSubjects] = useState([]);
    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [revaluationFee, setRevaluationFee] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');

    // Dummy data for revaluation subjects
    const dummyRevaluationSubjects = [
        {
            id: 1,
            subjectCode: 'CS101',
            subjectName: 'Data Structures and Algorithms',
            semester: 3,
            fee: 500,
            status: 'eligible',
            examDate: '2024-06-15',
            maxMarks: 100,
            obtainedMarks: 45,
            revaluationDeadline: '2024-06-30',
            department: 'Computer Science'
        },
        {
            id: 2,
            subjectCode: 'CS102',
            subjectName: 'Database Management Systems',
            semester: 3,
            fee: 500,
            status: 'eligible',
            examDate: '2024-06-18',
            maxMarks: 100,
            obtainedMarks: 38,
            revaluationDeadline: '2024-07-02',
            department: 'Computer Science'
        },
        {
            id: 3,
            subjectCode: 'CS103',
            subjectName: 'Operating Systems',
            semester: 3,
            fee: 500,
            status: 'eligible',
            examDate: '2024-06-20',
            maxMarks: 100,
            obtainedMarks: 42,
            revaluationDeadline: '2024-07-04',
            department: 'Computer Science'
        },
        {
            id: 4,
            subjectCode: 'CS104',
            subjectName: 'Computer Networks',
            semester: 3,
            fee: 500,
            status: 'eligible',
            examDate: '2024-06-22',
            maxMarks: 100,
            obtainedMarks: 35,
            revaluationDeadline: '2024-07-06',
            department: 'Computer Science'
        },
        {
            id: 5,
            subjectCode: 'CS105',
            subjectName: 'Software Engineering',
            semester: 3,
            fee: 500,
            status: 'eligible',
            examDate: '2024-06-25',
            maxMarks: 100,
            obtainedMarks: 40,
            revaluationDeadline: '2024-07-08',
            department: 'Computer Science'
        },
        {
            id: 6,
            subjectCode: 'CS106',
            subjectName: 'Web Technologies',
            semester: 3,
            fee: 500,
            status: 'eligible',
            examDate: '2024-06-28',
            maxMarks: 100,
            obtainedMarks: 48,
            revaluationDeadline: '2024-07-10',
            department: 'Computer Science'
        }
    ];

    useEffect(() => {
        if (isOpen) {
            fetchRevaluationSubjects();
        }
    }, [isOpen]);

    useEffect(() => {
        const total = selectedSubjects.reduce((sum, sub) => sum + (sub.fee || 500), 0);
        setRevaluationFee(total);
    }, [selectedSubjects]);

    const fetchRevaluationSubjects = async () => {
        setLoading(true);
        setError(null);
        setSelectedSubjects([]);
        setRevaluationFee(0);
        setSearchTerm('');

        try {
            await new Promise(resolve => setTimeout(resolve, 800));
            
            const subjects = dummyRevaluationSubjects.map(sub => ({
                ...sub,
                selected: false,
                fee: sub.fee || 500,
                isEligible: sub.status === 'eligible'
            }));
            setRevaluationSubjects(subjects);
        } catch (error) {
            console.error('Error fetching revaluation subjects:', error);
            setError(error.message || 'Failed to load revaluation subjects');
            setRevaluationSubjects([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubjectToggle = (subjectId) => {
        setRevaluationSubjects(prev => 
            prev.map(sub => 
                sub.id === subjectId ? { ...sub, selected: !sub.selected } : sub
            )
        );

        setSelectedSubjects(prev => {
            const isSelected = prev.some(s => s.id === subjectId);
            if (isSelected) {
                return prev.filter(s => s.id !== subjectId);
            } else {
                const subject = revaluationSubjects.find(s => s.id === subjectId);
                return subject ? [...prev, subject] : prev;
            }
        });
    };

    const handlePayment = async () => {
        if (selectedSubjects.length === 0) {
            setError('Please select at least one subject for revaluation');
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            onPaymentSuccess({
                subjects: selectedSubjects,
                totalFee: revaluationFee,
                rollNo: user?.roll_no,
                studentName: studentData?.name
            });
            
            onClose();
        } catch (error) {
            console.error('Revaluation payment error:', error);
            setError(error.message || 'Payment failed. Please try again.');
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        setSelectedSubjects([]);
        setRevaluationFee(0);
        setError(null);
        setIsProcessing(false);
        setSearchTerm('');
        onClose();
    };

    // Filter and search subjects
    const getFilteredSubjects = () => {
        let filtered = revaluationSubjects;
        
        if (searchTerm) {
            filtered = filtered.filter(sub => 
                sub.subjectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                sub.subjectCode.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        filtered = [...filtered].sort((a, b) => {
            if (sortBy === 'name') {
                return a.subjectName.localeCompare(b.subjectName);
            } else if (sortBy === 'code') {
                return a.subjectCode.localeCompare(b.subjectCode);
            } else if (sortBy === 'marks') {
                return b.obtainedMarks - a.obtainedMarks;
            } else if (sortBy === 'fee') {
                return a.fee - b.fee;
            }
            return 0;
        });
        
        return filtered;
    };

    const filteredSubjects = getFilteredSubjects();

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[95vh] flex flex-col overflow-hidden">
                {/* Modal Header - Fixed */}
                <div className="sticky top-0 z-20 bg-gradient-to-r from-teal-600 to-cyan-500 text-white p-6 rounded-t-3xl shadow-lg flex-shrink-0">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold">Revaluation Request</h2>
                                <p className="text-white/90 text-sm flex items-center gap-2">
                                    <span>Select subjects for revaluation</span>
                                    <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                                        {revaluationSubjects.length} subjects available
                                    </span>
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="text-white/80 hover:text-white transition p-2 hover:bg-white/20 rounded-xl"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Modal Body - Scrollable */}
                <div className="flex-1 overflow-y-auto px-6 pb-6 bg-gray-50">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent mb-4"></div>
                            <p className="text-gray-600 font-medium">Loading revaluation subjects...</p>
                            <p className="text-sm text-gray-400 mt-1">Please wait while we fetch your subjects</p>
                        </div>
                    ) : error && revaluationSubjects.length === 0 ? (
                        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
                            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-red-800 mb-2">Unable to Load Subjects</h3>
                            <p className="text-red-600 mb-4">{error}</p>
                            <button
                                onClick={fetchRevaluationSubjects}
                                className="px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition shadow-lg hover:shadow-xl"
                            >
                                Try Again
                            </button>
                        </div>
                    ) : revaluationSubjects.length === 0 ? (
                        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-12 text-center">
                            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-teal-800 mb-2">No Subjects Available</h3>
                            <p className="text-teal-600">No subjects are available for revaluation at this time.</p>
                            <p className="text-sm text-teal-500 mt-2">Please check back later or contact your department.</p>
                        </div>
                    ) : (
                        <>
                            {/* Search and Filter Bar */}
                            <div className="mb-6 flex flex-col sm:flex-row gap-3 sticky top-0 bg-gray-50 py-2 z-10">
                                <div className="flex-1 relative">
                                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Search subjects by name or code..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl bg-white focus:outline-none"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="px-4 py-2.5 border border-gray-300 rounded-xl bg-white focus:outline-none"
                                    >
                                        <option value="name">Sort by Name</option>
                                        <option value="code">Sort by Code</option>
                                        <option value="marks">Sort by Marks</option>
                                        <option value="fee">Sort by Fee</option>
                                    </select>
                                </div>
                            </div>

                            {/* Stats Bar */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sticky top-16 bg-gray-50 py-2 z-10">
                                <div className="bg-white rounded-xl p-3 border border-gray-200 text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Total Subjects</p>
                                    <p className="text-xl font-bold text-gray-800">{revaluationSubjects.length}</p>
                                </div>
                                <div className="bg-white rounded-xl p-3 border border-gray-200 text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Eligible</p>
                                    <p className="text-xl font-bold text-green-600">
                                        {revaluationSubjects.filter(s => s.isEligible).length}
                                    </p>
                                </div>
                                <div className="bg-white rounded-xl p-3 border border-gray-200 text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Selected</p>
                                    <p className="text-xl font-bold text-teal-600">{selectedSubjects.length}</p>
                                </div>
                                <div className="bg-white rounded-xl p-3 border border-gray-200 text-center shadow-sm">
                                    <p className="text-xs text-gray-500">Total Fee</p>
                                    <p className="text-xl font-bold text-orange-600">₹{revaluationFee}</p>
                                </div>
                            </div>

                            {/* Subject List with compact field display */}
                            <div className="space-y-3 mb-6">
                                {filteredSubjects.map((subject) => (
                                    <div
                                        key={subject.id}
                                        onClick={() => subject.isEligible && handleSubjectToggle(subject.id)}
                                        className={`relative group p-4 border-2 rounded-xl transition-all duration-300 ${
                                            subject.selected 
                                                ? 'border-teal-500 bg-teal-50 shadow-lg shadow-teal-100' 
                                                : subject.isEligible 
                                                    ? 'border-gray-200 hover:border-teal-300 hover:bg-gray-50 cursor-pointer' 
                                                    : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                                        }`}
                                    >
                                        <div className="flex items-start gap-4">
                                            {/* Checkbox */}
                                            <div className="flex-shrink-0 pt-1">
                                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                                    subject.selected 
                                                        ? 'border-teal-500 bg-teal-500 shadow-lg shadow-teal-200' 
                                                        : subject.isEligible
                                                            ? 'border-gray-300 group-hover:border-teal-400'
                                                            : 'border-gray-200'
                                                }`}>
                                                    {subject.selected && (
                                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Subject Details - Simplified with only essential fields */}
                                            <div className="flex-1">
                                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 flex-wrap">
                                                            <h4 className="font-semibold text-gray-800 text-lg">{subject.subjectName}</h4>
                                                            <span className="bg-teal-100 text-teal-700 px-2 py-0.5 rounded-md text-xs font-medium">
                                                                {subject.subjectCode}
                                                            </span>
                                                            {subject.isEligible ? (
                                                                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-md text-xs font-medium flex items-center gap-1">
                                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                    Eligible
                                                                </span>
                                                            ) : (
                                                                <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-md text-xs font-medium">
                                                                    Not Eligible
                                                                </span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Display fields in a compact grid */}
                                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-sm">
                                                            <span className="text-gray-600 flex items-center gap-1">
                                                                <span className="text-xs text-gray-400">Sem:</span>
                                                                <span className="font-medium">{subject.semester}</span>
                                                            </span>
                                                            <span className="text-gray-600 flex items-center gap-1">
                                                                <span className="text-xs text-gray-400">Marks:</span>
                                                                <span className={`font-medium ${subject.obtainedMarks >= 40 ? 'text-green-600' : 'text-red-600'}`}>
                                                                    {subject.obtainedMarks}/{subject.maxMarks}
                                                                </span>
                                                            </span>
                                                            <span className="text-gray-600 flex items-center gap-1 col-span-2 sm:col-span-1">
                                                                <span className="text-xs text-gray-400">Dept:</span>
                                                                <span className="font-medium">{subject.department}</span>
                                                            </span>
                                                            <span className="text-gray-600 flex items-center gap-1 col-span-2 sm:col-span-1">
                                                                <span className="text-xs text-gray-400">Exam:</span>
                                                                <span className="font-medium">{new Date(subject.examDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                                                            </span>
                                                            <span className="text-gray-600 flex items-center gap-1 col-span-2 sm:col-span-1">
                                                                <span className="text-xs text-gray-400">Deadline:</span>
                                                                <span className="font-medium">{new Date(subject.revaluationDeadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-right mt-3 sm:mt-0">
                                                        <p className="text-2xl font-bold text-teal-600">₹{subject.fee || 500}</p>
                                                        <p className="text-xs text-gray-400">per subject</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {filteredSubjects.length === 0 && searchTerm && (
                                <div className="text-center py-8">
                                    <p className="text-gray-500">No subjects found matching your search.</p>
                                </div>
                            )}

                            {/* Summary Section */}
                            {selectedSubjects.length > 0 && (
                                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-6 border-2 border-teal-200 shadow-lg">
                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                        <div>
                                            <p className="text-sm text-gray-600 flex items-center gap-2">
                                                <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                                </svg>
                                                Selected Subjects
                                            </p>
                                            <p className="text-3xl font-bold text-gray-800">{selectedSubjects.length}</p>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {selectedSubjects.map(sub => (
                                                    <span key={sub.id} className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                                                        {sub.subjectCode}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="text-right bg-white/50 p-4 rounded-xl w-full md:w-auto">
                                            <p className="text-sm text-gray-600">Total Revaluation Fee</p>
                                            <p className="text-4xl font-bold text-teal-600">₹{revaluationFee.toLocaleString()}</p>
                                            <p className="text-xs text-gray-400 mt-1">Including all selected subjects</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                                    <div className="flex items-start gap-3">
                                        <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <p className="text-red-700 text-sm">{error}</p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Modal Footer - Fixed */}
                <div className="sticky bottom-0 z-20 bg-white border-t-2 border-gray-100 p-6 rounded-b-3xl shadow-lg flex-shrink-0">
                    <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                        <div className="flex items-center gap-4">
                            <div className="hidden sm:block">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                                    <span className="text-sm text-gray-600">Secure Payment</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <span>256-bit encrypted</span>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                            <button
                                onClick={handleClose}
                                className="px-8 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePayment}
                                disabled={selectedSubjects.length === 0 || isProcessing}
                                className={`px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 min-w-[180px] ${
                                    selectedSubjects.length > 0 && !isProcessing
                                        ? 'bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95'
                                        : 'bg-gray-300 cursor-not-allowed'
                                }`}
                            >
                                {isProcessing ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                        </svg>
                                        Pay ₹{revaluationFee.toLocaleString()}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                    {selectedSubjects.length > 0 && !isProcessing && (
                        <div className="flex items-center justify-center gap-2 mt-3">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-xs text-gray-500 text-center">
                                By proceeding, you agree to the revaluation terms and conditions. This payment is non-refundable.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RevaluationModal;